import React, { useState, useMemo, useRef, useEffect } from "react";
import "./index.scss";

export const ClientMultiSelect = ({
  clients,
  selectedClients,
  onChange,
  loading = false,
  placeholder = "Search clients...",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [newContact, setNewContact] = useState({ email: "", firstName: "", lastName: "", phone: "" });
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedIds = useMemo(
    () => new Set(selectedClients.map((c) => c.id)),
    [selectedClients]
  );

  const filteredClients = useMemo(() => {
    return clients
      .filter((client) => !selectedIds.has(client.id || client._id))
      .filter((client) => {
        const searchLower = searchTerm.toLowerCase();
        const email = (client.email || "").toLowerCase();
        const fullName = `${client.firstName || ""} ${client.lastName || ""}`.toLowerCase();
        return email.includes(searchLower) || fullName.includes(searchLower);
      });
  }, [clients, selectedIds, searchTerm]);

  // Check if searchTerm is a valid email not in clients list or already selected
  const isNewEmail = useMemo(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(searchTerm)) return false;
    const searchLower = searchTerm.toLowerCase();
    const inClients = clients.some((c) => c.email?.toLowerCase() === searchLower);
    const inSelected = selectedClients.some((c) => c.clientEmail?.toLowerCase() === searchLower);
    return !inClients && !inSelected;
  }, [searchTerm, clients, selectedClients]);

  // Helper to get fallback name from email if firstName/lastName are empty
  const getNameWithFallback = (client) => {
    const emailUsername = (client.email || "").split("@")[0] || "Client";
    return {
      firstName: client.firstName || emailUsername,
      lastName: client.lastName || "",
    };
  };

  const handleSelectClient = (client) => {
    const names = getNameWithFallback(client);
    const newClient = {
      id: client.id || client._id,
      clientId: client.id || client._id,
      clientEmail: client.email,
      firstName: names.firstName,
      lastName: names.lastName,
    };
    onChange([...selectedClients, newClient]);
    setSearchTerm("");
  };

  const handleRemoveClient = (clientId) => {
    onChange(selectedClients.filter((c) => c.id !== clientId));
  };

  const handleSelectAll = () => {
    const allClients = clients.map((client) => {
      const names = getNameWithFallback(client);
      return {
        id: client.id || client._id,
        clientId: client.id || client._id,
        clientEmail: client.email,
        firstName: names.firstName,
        lastName: names.lastName,
      };
    });
    onChange(allClients);
    setShowDropdown(false);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const handleAddNewContact = () => {
    const emailUsername = newContact.email.split("@")[0] || "Contact";
    const invitee = {
      id: `new-${Date.now()}`,
      clientId: null,
      clientEmail: newContact.email,
      firstName: newContact.firstName || emailUsername,
      lastName: newContact.lastName || "",
      phone: newContact.phone || "",
    };
    onChange([...selectedClients, invitee]);
    setNewContact({ email: "", firstName: "", lastName: "", phone: "" });
    setShowNewContactForm(false);
    setSearchTerm("");
  };

  const handleStartNewContact = () => {
    setNewContact({ email: searchTerm, firstName: "", lastName: "", phone: "" });
    setShowNewContactForm(true);
  };

  const handleCancelNewContact = () => {
    setShowNewContactForm(false);
    setNewContact({ email: "", firstName: "", lastName: "", phone: "" });
  };

  return (
    <div className="client-multi-select" ref={containerRef}>
      {/* Selected clients chips - show count if more than 15 */}
      {selectedClients.length > 0 && selectedClients.length <= 15 && (
        <div className="selected-clients">
          {selectedClients.map((client) => (
            <span key={client.id} className="client-chip">
              {client.firstName} {client.lastName}
              <button
                type="button"
                onClick={() => handleRemoveClient(client.id)}
                aria-label={`Remove ${client.firstName}`}
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}
      {selectedClients.length > 15 && (
        <div className="selected-clients-summary">
          <span className="client-count-badge">
            {selectedClients.length} clients selected
          </span>
        </div>
      )}

      {/* Selected count and clear all - hide count when > 15 since badge shows it */}
      {selectedClients.length > 0 && (
        <div className="selection-info">
          {selectedClients.length <= 15 && (
            <span className="selected-count">
              {selectedClients.length} client{selectedClients.length > 1 ? "s" : ""} selected
            </span>
          )}
          <button type="button" className="clear-all" onClick={handleClearAll}>
            Clear all
          </button>
        </div>
      )}

      {/* Search input */}
      <input
        type="text"
        className="custom-input bg-transparent search-input"
        placeholder={placeholder}
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setShowDropdown(true);
        }}
        onFocus={() => setShowDropdown(true)}
      />

      {/* Dropdown */}
      {showDropdown && !showNewContactForm && (
        <div className="dropdown-menu show">
          {/* Add new contact option when email is valid and not in list */}
          {isNewEmail && (
            <button
              type="button"
              className="dropdown-item add-new-contact"
              onClick={handleStartNewContact}
            >
              <span className="add-icon">+</span>
              Add "{searchTerm}" as new contact
            </button>
          )}

          {/* Select All option */}
          {filteredClients.length > 0 && selectedClients.length < clients.length && (
            <button
              type="button"
              className="dropdown-item select-all"
              onClick={handleSelectAll}
            >
              Select All ({clients.length} clients)
            </button>
          )}

          {loading ? (
            <div className="dropdown-item disabled">Loading clients...</div>
          ) : filteredClients.length === 0 ? (
            <div className="dropdown-item disabled">
              {searchTerm ? "No clients found" : "All clients selected"}
            </div>
          ) : (
            filteredClients.slice(0, 50).map((client) => (
              <button
                key={client.id || client._id}
                type="button"
                className="dropdown-item"
                onClick={() => handleSelectClient(client)}
              >
                <span className="client-name">
                  {client.firstName} {client.lastName}
                </span>
                <span className="client-email">{client.email}</span>
              </button>
            ))
          )}

          {filteredClients.length > 50 && (
            <div className="dropdown-item disabled">
              ...and {filteredClients.length - 50} more. Type to filter.
            </div>
          )}
        </div>
      )}

      {/* New contact form */}
      {showNewContactForm && (
        <div className="new-contact-form">
          <div className="new-contact-email">
            Adding: <strong>{newContact.email}</strong>
          </div>
          <div className="form-row">
            <input
              type="text"
              className="custom-input bg-transparent"
              placeholder="First name (optional)"
              value={newContact.firstName}
              onChange={(e) => setNewContact({ ...newContact, firstName: e.target.value })}
            />
            <input
              type="text"
              className="custom-input bg-transparent"
              placeholder="Last name (optional)"
              value={newContact.lastName}
              onChange={(e) => setNewContact({ ...newContact, lastName: e.target.value })}
            />
          </div>
          <div className="form-row">
            <input
              type="tel"
              className="custom-input bg-transparent"
              placeholder="Phone (optional)"
              value={newContact.phone}
              onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
            />
          </div>
          <div className="form-actions">
            <button type="button" className="btn-add" onClick={handleAddNewContact}>
              Add Contact
            </button>
            <button type="button" className="btn-cancel" onClick={handleCancelNewContact}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientMultiSelect;
